import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Grid, Paper } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { InterventionForm } from './components/InterventionForm';
import { InterventionItem } from './components/InterventionItem';
import { CaseDetails } from './components/CaseDetails';
import { mockStudents } from '@/data/mockStudents';
import { Intervention, Student } from '@/types';
import { Case } from '@/features/cases/types';

export const CaseManagement: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState<(Intervention & { student?: Student }) | null>(null);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);

  // Mock initial interventions with student data
  const initialInterventions: Array<Intervention & { student: Student }> = mockStudents.slice(0, 8).map((student, index) => ({
    id: `int-${index + 1}`,
    type: 'individual' as const,
    date: new Date().toISOString(),
    notes: 'Initial counseling session',
    outcome: 'Positive progress noted',
    status: index < 6 ? 'in_process' as const : 'completed' as const,
    student,
    tasks: [],
  }));

  const [interventions, setInterventions] = useState(initialInterventions);

  const handleAddIntervention = () => {
    setSelectedIntervention(null);
    setIsFormOpen(true);
  };

  const handleEditIntervention = (intervention: Intervention & { student: Student }) => {
    setSelectedIntervention(intervention);
    setIsFormOpen(true);
  };

  const handleViewDetails = (intervention: Intervention & { student: Student }) => {
    const caseData: Case = {
      id: intervention.id,
      title: `Case: ${intervention.student.firstName} ${intervention.student.lastName} (Grade ${intervention.student.grade})`,
      description: intervention.notes,
      studentId: intervention.student.id,
      category: 'attendance',
      priority: 'medium',
      status: intervention.status === 'completed' ? 'closed' : 'open',
      assignedTo: 'counselor',
      createdAt: intervention.date,
      updatedAt: new Date().toISOString(),
      tasks: []
    };
    setSelectedCase(caseData);
  };

  const handleSaveIntervention = (intervention: Omit<Intervention, 'id'> & { student?: Student; id?: string }) => {
    if (!intervention.student) return;

    if (intervention.id) {
      // Handle update
      setInterventions(prev => prev.map(i => {
        if (i.id === intervention.id) {
          // Update intervention and handle status change
          const updatedIntervention = { 
            ...intervention, 
            student: intervention.student!,
            // Update outcome if status changed to completed
            outcome: intervention.status === 'completed' ? 
              'Case objectives achieved' : 
              intervention.outcome
          } as any;
          return updatedIntervention;
        }
        return i;
      }));
    } else {
      // Handle create
      const newIntervention = {
        ...intervention,
        id: `int-${Date.now()}`,
        student: intervention.student
      };
      setInterventions(prev => [newIntervention, ...prev] as any);
    }
    setIsFormOpen(false);
    setSelectedIntervention(null);
  };

  const handleDeleteIntervention = (id: string) => {
    if (window.confirm('Are you sure you want to delete this intervention?')) {
      setInterventions(prev => prev.filter(i => i.id !== id));
      if (selectedCase?.id === id) {
        setSelectedCase(null);
      }
    }
  };

  const handleUpdateCase = (updatedCase: Case) => {
    setSelectedCase(updatedCase);
    // Update the corresponding intervention status
    setInterventions(prev => prev.map(i => {
      if (i.id === updatedCase.id) {
        const newStatus = updatedCase.status === 'closed' ? 'completed' : 'in_process';
        return {
          ...i,
          status: newStatus,
          outcome: newStatus === 'completed' ? 'Case objectives achieved' : i.outcome
        };
      }
      return i;
    }));
  };

  const activeInterventions = interventions.filter(i => i.status !== 'completed');
  const completedInterventions = interventions.filter(i => i.status === 'completed');

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Case Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddIntervention}
        >
          New Intervention
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Paper 
              sx={{ 
                height: '100%', 
                overflow: 'auto',
                bgcolor: 'background.default',
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">Active Cases</Typography>
                <Typography variant="body2" color="text.secondary">
                  ({activeInterventions.length})
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {activeInterventions.map((intervention) => (
                  <Grid item xs={12} key={intervention.id}>
                    <InterventionItem
                      intervention={intervention}
                      onEdit={() => handleEditIntervention(intervention)}
                      onDelete={handleDeleteIntervention}
                      onViewDetails={() => handleViewDetails(intervention)}
                    />
                  </Grid>
                ))}
                {activeInterventions.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No active cases
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Paper 
              sx={{ 
                height: '100%', 
                overflow: 'auto',
                bgcolor: 'background.default',
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">Completed Cases</Typography>
                <Typography variant="body2" color="text.secondary">
                  ({completedInterventions.length})
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {completedInterventions.map((intervention) => (
                  <Grid item xs={12} key={intervention.id}>
                    <InterventionItem
                      intervention={intervention}
                      onEdit={() => handleEditIntervention(intervention)}
                      onDelete={handleDeleteIntervention}
                      onViewDetails={() => handleViewDetails(intervention)}
                    />
                  </Grid>
                ))}
                {completedInterventions.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No completed cases
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <InterventionForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSaveIntervention}
        initialData={selectedIntervention}
      />

      <CaseDetails
        caseData={selectedCase}
        open={!!selectedCase}
        onClose={() => setSelectedCase(null)}
        onUpdate={handleUpdateCase}
      />
    </Box>
  );
};